/**
 * Where the light does not reach.
 *
 * A world made of one pale material has no colour left to describe its shape
 * with, so the shape has to come from shading — and the shading a real room
 * has most of is not the sun, it is the light that never arrives because
 * another surface is in the way. Corners are dark. The strip under a shelf is
 * dark. The metre of wall behind a pillar is dark. Take that away and a white
 * room is a white rectangle.
 *
 * Real-time ambient occlusion is a screen-space pass and the tier that needs
 * this most is the tier that cannot run one. But the level is boxes on a grid
 * and it never moves, so the occlusion can simply be computed once, per
 * vertex, against the solids themselves — no screen, no depth buffer, no
 * per-frame cost at all. It goes in the vertex colour channel, which is free
 * because nothing lives there since the per-solid tint was removed.
 */

import * as THREE from 'three';
import type { Solid } from 'levelspec';

/** How coarse the occupancy lookup is, in metres. */
const CELL = 0.5;

/**
 * Which cells the level's solids fill.
 *
 * A hash set of cell keys rather than a tree: every query is one string lookup
 * and there are a few hundred thousand of them, which is the only reason this
 * is cheap enough to do at load.
 */
export class Occupancy {
  private readonly cells = new Set<string>();

  constructor(solids: Solid[]) {
    for (const s of solids) {
      // Canonical space is Z-up; everything here works in the renderer's
      // frame, so the conversion happens once, on the way in.
      const x0 = Math.floor(s.box.min[0] / CELL);
      const x1 = Math.ceil(s.box.max[0] / CELL);
      const y0 = Math.floor(s.box.min[2] / CELL);
      const y1 = Math.ceil(s.box.max[2] / CELL);
      const z0 = Math.floor(-s.box.max[1] / CELL);
      const z1 = Math.ceil(-s.box.min[1] / CELL);
      // A solid the size of a floor slab is thousands of cells and there are
      // hundreds of them; the interior of a slab is never queried, so only the
      // shell is stored. Everything asking is standing on a surface.
      for (let x = x0; x < x1; x++)
        for (let y = y0; y < y1; y++)
          for (let z = z0; z < z1; z++) {
            const inside =
              x > x0 && x < x1 - 1 && y > y0 && y < y1 - 1 && z > z0 && z < z1 - 1;
            if (!inside) this.cells.add(`${x},${y},${z}`);
          }
    }
  }

  at(x: number, y: number, z: number): boolean {
    return this.cells.has(
      `${Math.floor(x / CELL)},${Math.floor(y / CELL)},${Math.floor(z / CELL)}`,
    );
  }

  get size(): number {
    return this.cells.size;
  }
}

/**
 * Directions on the hemisphere around a normal.
 *
 * Fixed rather than random: two builds of the same map have to shade the same,
 * and a stochastic set at this sample count would visibly speckle.
 */
const HEMISPHERE: [number, number, number][] = [
  [0, 1, 0],
  [0.85, 0.5, 0], [-0.85, 0.5, 0], [0, 0.5, 0.85], [0, 0.5, -0.85],
  [0.6, 0.5, 0.6], [-0.6, 0.5, 0.6], [0.6, 0.5, -0.6], [-0.6, 0.5, -0.6],
  [0.98, 0.17, 0], [-0.98, 0.17, 0], [0, 0.17, 0.98], [0, 0.17, -0.98],
];
/** How far out to look. Near samples make the crease, far ones make the room. */
const RADII = [0.35, 0.95, 2.1];

/**
 * Darken every vertex by how much of the sky it cannot see.
 *
 * Writes into the geometry's existing colour attribute, so a mesh that was
 * flat white comes back with its corners, its ceilings and the undersides of
 * its ledges shaded — and the material does not have to know it happened.
 */
export function bakeContact(geo: THREE.BufferGeometry, world: Occupancy, strength = 1): void {
  const pos = geo.getAttribute('position');
  const nrm = geo.getAttribute('normal');
  const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
  if (!pos || !nrm || !col) return;

  // A basis around the vertex normal, so the hemisphere is oriented into the
  // room rather than into the wall.
  const n = new THREE.Vector3();
  const t = new THREE.Vector3();
  const b = new THREE.Vector3();
  const up = new THREE.Vector3();

  for (let i = 0; i < pos.count; i++) {
    n.fromBufferAttribute(nrm, i);
    if (n.lengthSq() < 1e-8) continue;
    up.set(0, 1, 0);
    if (Math.abs(n.y) > 0.9) up.set(1, 0, 0);
    t.crossVectors(up, n).normalize();
    b.crossVectors(n, t);

    const px = pos.getX(i);
    const py = pos.getY(i);
    const pz = pos.getZ(i);
    let open = 0;
    let taken = 0;
    for (const [hx, hy, hz] of HEMISPHERE) {
      // Hemisphere direction into world space: hy is along the normal.
      const dx = t.x * hx + n.x * hy + b.x * hz;
      const dy = t.y * hx + n.y * hy + b.y * hz;
      const dz = t.z * hx + n.z * hy + b.z * hz;
      for (let r = 0; r < RADII.length; r++) {
        const d = RADII[r];
        // Nudged off the surface first, or every sample hits the face it
        // started on and the whole level bakes black.
        const hit = world.at(
          px + n.x * 0.12 + dx * d,
          py + n.y * 0.12 + dy * d,
          pz + n.z * 0.12 + dz * d,
        );
        // Near occluders matter more than far ones: a wall at thirty
        // centimetres is a crease, the same wall at two metres is a room.
        const weight = 1 / (1 + r * 1.6);
        open += weight;
        if (hit) taken += weight;
      }
    }
    /*
     * Enough to find the corner, not enough to lose the wall.
     *
     * At 0.88 this took a fully-enclosed vertex down to twelve per cent, and
     * since most of a room's surface is somewhat occluded, most of a white
     * room came out mid-grey — the shell stopped reading as porcelain and the
     * frame measured as one flat middle tone. Occlusion is a hint about form,
     * not a second light source.
     */
    const ao = open > 0 ? 1 - (taken / open) * 0.5 * strength : 1;
    // Never to black. A corner you cannot see into is not depth, it is a hole,
    // and this is the only shading some surfaces will ever get.
    const v = Math.max(0.52, ao);
    col.setXYZ(i, col.getX(i) * v, col.getY(i) * v, col.getZ(i) * v);
  }
  col.needsUpdate = true;
}
